import { ref, onMounted } from "vue";

export type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "app-theme";

const currentTheme = ref<Theme>("light");

export function useTheme() {
    const initializeTheme = () => {
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;

        if (savedTheme) {
            currentTheme.value = savedTheme;
        } else {
            const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            currentTheme.value = prefersDark ? "dark" : "light";
        }

        applyTheme(currentTheme.value);
    };

    const applyTheme = (theme: Theme) => {
        document.documentElement.setAttribute("data-theme", theme);
    };

    const toggleTheme = () => {
        currentTheme.value = currentTheme.value === "light" ? "dark" : "light";
        localStorage.setItem(THEME_STORAGE_KEY, currentTheme.value);
        applyTheme(currentTheme.value);
    };

    const setTheme = (theme: Theme) => {
        currentTheme.value = theme;
        localStorage.setItem(THEME_STORAGE_KEY, theme);
        applyTheme(theme);
    };

    onMounted(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem(THEME_STORAGE_KEY)) {
                currentTheme.value = e.matches ? "dark" : "light";
                applyTheme(currentTheme.value);
            }
        };

        mediaQuery.addEventListener("change", handleChange);

        return () => {
            mediaQuery.removeEventListener("change", handleChange);
        };
    });

    return {
        currentTheme,
        initializeTheme,
        toggleTheme,
        setTheme,
    };
}
